import { Dispatchers, toOption } from '@client/new/helpers/types';
import {
  ContentRootTree,
  ContentTab,
  ContentTree,
  Content,
  ContentType,
  ExtrasContent,
} from '../model/extras.domain';
import { ExtrasVMActionMap, Filters } from './extras.viewModel';
import { BasicMenuItem } from '@client/new/components/BasicMenu/BasicMenu';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faImage, faLink } from '@fortawesome/pro-regular-svg-icons';
import { v4 } from 'uuid';

export const filterTypeOptions = [
  toOption('ContentLink', 'Links'),
  toOption('ContentLegacyGuide', 'Legacy Guides'),
  toOption('ContentSlides', 'Slides'),
  // toOption('ContentPDF', 'PDFs'),
];

export const typeToLabel = (type: ContentType): string => {
  switch (type) {
    case 'ContentLegacyGuide':
      return 'Guide';
    case 'ContentLink':
      return 'Link';
    case 'ContentPDF':
      return 'PDF';
    case 'ContentSlides':
      return 'Slides';
    default:
      return '';
  }
};

export function toLegacyGuideURL(id: number): string {
  return `https://fitner-web.s3.amazonaws.com/hc/nutrition/${id}/cover.jpg`;
}

export const filterExtrasVM = (
  vm: ExtrasContent,
  search: string,
  filters: Filters,
): ExtrasContent['contentMap'] => {
  const { contentMap } = vm;

  const filteredContentMap: Record<string, Content> = Object.values(contentMap)
    .filter((item) => {
      if (item.type === 'ContentTab') return true;

      const matchesType = !filters.type || item.type === filters.type;

      const title = item.value.title || '';
      const matchesSearch =
        !search || title.toLowerCase().includes(search.toLowerCase());

      return matchesType && matchesSearch;
    })
    .reduce<Record<string, Content>>((acc, item) => {
      acc[item.value.id] = item;
      return acc;
    }, {});

  return filteredContentMap;
};

export const toExtrasMenuItems = (
  dispatch: Dispatchers<ExtrasVMActionMap>['dispatch'],
): BasicMenuItem[] => [
  {
    label: 'Link',
    onClick: () => dispatch('Create', 'ContentLink'),
    icon: <FontAwesomeIcon icon={faLink} />,
  },
  {
    label: 'Slides',
    onClick: () => dispatch('Create', 'ContentSlides'),
    icon: <FontAwesomeIcon icon={faImage} />,
  },
];

type FormValues = {
  [K in Content as K['type']]: K['value'];
};

export const defaultValues: FormValues = {
  ['ContentLegacyGuide']: {
    title: '',
    contentId: 0,
    count: 0,
    id: '',
    isPublished: false,
  },
  ['ContentLink']: {
    title: '',
    url: '',
    coverImage: '',
    id: '',
    isPublished: false,
  },
  ['ContentTab']: {
    id: '',
    name: '',
    isPublished: false,
  },
  ['ContentSlides']: {
    id: '',
    title: '',
    coverURL: '',
    isPublished: false,
    downloadable: false,
  },
  ['ContentPDF']: {
    id: '',
    title: '',
    url: '',
    coverURL: '',
    downloadable: false,
    isPublished: false,
  },
  ['ContentImage']: {
    id: '',
    title: '',
    url: '',
    isPublished: true,
    file: null,
  },
};

export function toggleIsPublished<T extends Content>(content: T): T {
  return {
    ...content,
    value: {
      ...content.value,
      isPublished: !content.value.isPublished,
    },
  };
}

const copyExcluding = (obj: any, excludeKeys: string[]) => {
  const result: any = {};
  Object.keys(obj).forEach((key) => {
    if (!excludeKeys.includes(key)) {
      result[key] = obj[key];
    }
  });
  return result;
};

export function updateTreeItems(
  tree: ContentRootTree | ContentTree,
  targetId: string,
  newItems: ContentTree[],
): ContentRootTree | ContentTree {
  const updateNode = (node: ContentTree | ContentRootTree): ContentTree => ({
    id: node.id,
    ...copyExcluding(node, ['id', 'items']),
    ...(node.items?.length && {
      items:
        node.id === targetId
          ? newItems.map((newItem) =>
              updateNode({
                ...newItem,
                items:
                  node.items?.find((existing) => existing.id === newItem.id)
                    ?.items || newItem.items,
              }),
            )
          : node.items.map(updateNode),
    }),
  });

  return updateNode(tree);
}

export const emptyTab = (name: string): ContentTab => ({
  type: 'ContentTab',
  value: {
    id: v4(),
    isPublished: false,
    name,
  },
});

export const getContentName = (c: Content): string => {
  switch (c.type) {
    case 'ContentLegacyGuide':
      return c.value.title || 'Untitled Guide';
    case 'ContentLink':
      return c.value.title || 'Untitled Link';
    case 'ContentTab':
      return c.value.name || 'Untitled Tab';
    default:
      return 'Unknown Content';
  }
};

export const getSlidesFinalOrder = (p: {
  currentOrder: ContentTree[];
  order: string[];
  toDelete: string[];
}) => {
  const { currentOrder, order, toDelete } = p;

  let finalOrder: string[] = [];
  const allCurrentChildren = currentOrder.filter(
    (child) => !toDelete.includes(child.id),
  );

  if (order.length > 0) {
    const existingItemsInOrder = order.filter(
      (id) =>
        !toDelete.includes(id) &&
        allCurrentChildren.some((child) => child.id === id),
    );

    const existingItemsNotInOrder = allCurrentChildren
      .filter((child) => !order.includes(child.id))
      .map((child) => child.id);

    const newItemsInOrder = order.filter(
      (id) =>
        !toDelete.includes(id) &&
        !allCurrentChildren.some((child) => child.id === id),
    );

    finalOrder = [
      ...existingItemsNotInOrder,
      ...existingItemsInOrder,
      ...newItemsInOrder,
    ];
  } else {
    finalOrder = allCurrentChildren.map((child) => child.id);
  }

  return finalOrder;
};
